import { createContext, useContext, useMemo, useState, useEffect } from "react";

/**
 * AppState global
 * - tenantId: inmutable durante la sesión (de .env o de localStorage)
 * - activeBranchId: sede activa para filtros
 */
type AppState = {
  tenantId: string | null;
  activeBranchId: string;
  setActiveBranchId: (id: string) => void;
};

const AppCtx = createContext<AppState | undefined>(undefined);

// keys de persistencia
const KEY_BRANCH = "gym.activeBranchId";
const KEY_TENANT = "gym.tenantId";

export function AppProvider({ children }: { children: React.ReactNode }) {
  const envTenant = (import.meta.env.VITE_TENANT_ID as string | undefined) ?? null;

  // 1) Tenant: resolver inmediatamente (evita la ventana en la que es null)
  const initialTenant = (() => {
    try {
      const saved = localStorage.getItem(KEY_TENANT);
      return (saved || envTenant || null) as string | null;
    } catch {
      return envTenant || null;
    }
  })();
  const [tenantId] = useState<string | null>(initialTenant);

  // Persistir tenant si viene vacío y luego aparece (o si cambia)
  useEffect(() => {
    if (tenantId) {
      try {
        localStorage.setItem(KEY_TENANT, tenantId);
      } catch {
        /* noop */
      }
    }
  }, [tenantId]);

  // 2) Branch: también resolver inmediatamente
  const initialBranch = (() => {
    try {
      return localStorage.getItem(KEY_BRANCH) || "main";
    } catch {
      return "main";
    }
  })();
  const [activeBranchId, setActiveBranchIdState] = useState<string>(initialBranch);

  function setActiveBranchId(id: string) {
    setActiveBranchIdState(id);
    try {
      localStorage.setItem(KEY_BRANCH, id);
    } catch {
      /* noop */
    }
  }

  const value = useMemo<AppState>(() => ({ tenantId, activeBranchId, setActiveBranchId }), [tenantId, activeBranchId]);

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useApp() {
  const ctx = useContext(AppCtx);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
